import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AppModule } from './app.module';
import { MainLayout } from "./main-layout/main-layout";
import { NotificationService } from './shared/notification.service';
import { AuthService } from './auth.service';
import { WebsocketsService } from './shared/websockets.service';
import { delay, repeat, retryWhen, Subscription, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { SharedDataService } from './shared/shareddata.service';
import { LocationUpdate } from "./shared/location-update/location-update";
import { AppUpdateService } from './shared/app-update.service';
import { MobileDashboardService } from './shared/mobile-dashboard.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, AppModule, MainLayout, LocationUpdate],
  templateUrl: './app.html',
  styleUrl: './app.less'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('StockFinder Truck Breakdown Maintenance');

  private websocketSubscription: Subscription;
  private authSubscription: Subscription;
  private metadataSubscription: Subscription;
  private callcentreOperatorSelectionSubscription: Subscription;
  private updateCheckInterval: any = null;
  private visibilityHandler: (() => void) | null = null;
  private showCallcentreOperatorModalAfterMetadata = false;

  public isCallcentreOperatorModalVisible = false;
  public selectedCallcentreOperatorId: number | null = null;

  constructor(private route: Router,
    private websockets: WebsocketsService,
    private authService: AuthService,
    private notification: NotificationService,
    private sharedDataService: SharedDataService,
    private appUpdateService: AppUpdateService,
    public mobileDashboard: MobileDashboardService
  ) {}

  public ngOnInit(): void {
    this.mobileDashboard.start();
    // Initialize WebSocket connection

    this.metadataSubscription = this.sharedDataService.metadataLoaded.subscribe(() => {
      if (this.showCallcentreOperatorModalAfterMetadata) {
        const storedOperator = this.authService.getStoredCallcentreOperator();
        if (storedOperator) {
          // This restores both the selection and AuthService.callcentreOperator.
          this.selectCallcentreOperator(storedOperator.userId);
        }
        this.isCallcentreOperatorModalVisible = true;
      } else if (this.authService.user?.isWhatsappOnlyNotificationRecipient) {
        const storedOperator = this.authService.getStoredCallcentreOperator();
        if (storedOperator) {
          // Restore the previous selection after a page refresh without reopening the modal.
          this.selectCallcentreOperator(storedOperator.userId);
        }
      } else {
        const loggedInOperator = this.availableCallcentreOperators.find(
          operator => operator.userId === this.authService.user?.userId
        );
        if (loggedInOperator) {
          this.selectCallcentreOperator(loggedInOperator.userId);
        }
      }
      this.showCallcentreOperatorModalAfterMetadata = false;
    });

    this.callcentreOperatorSelectionSubscription = this.sharedDataService.callcentreOperatorSelectionRequested.subscribe(() => {
      this.selectedCallcentreOperatorId = this.authService.callcentreOperator?.userId ?? null;
      if (this.sharedDataService.isLoadingMetaData.value) {
        this.showCallcentreOperatorModalAfterMetadata = true;
        return;
      }
      this.isCallcentreOperatorModalVisible = true;
    });

    this.authSubscription = this.authService.loginSubscription.subscribe(({ user, source }) => {
      console.log('Everything is refreshing with source:', source);
      this.connectWebSockets();
      this.showCallcentreOperatorModalAfterMetadata =
        source === 'interactiveLogin' && user.isWhatsappOnlyNotificationRecipient;
      this.selectedCallcentreOperatorId = null;
      this.authService.clearCallcentreOperatorFromMemory();
      this.sharedDataService.loadMetaData();
    });

    // Periodically check for SW updates (every 2 minutes)
    this.updateCheckInterval = setInterval(() => {
      this.appUpdateService.checkForUpdate();
    }, 2 * 60 * 1000);

    // Check for updates when the page becomes visible again (e.g. WhatsApp browser tab resume)
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.appUpdateService.checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  public ngOnDestroy(): void {
    // Clean up WebSocket connection
    if (this.websocketSubscription) {
      this.websocketSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.metadataSubscription) {
      this.metadataSubscription.unsubscribe();
    }
    if (this.callcentreOperatorSelectionSubscription) {
      this.callcentreOperatorSelectionSubscription.unsubscribe();
    }
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
      this.updateCheckInterval = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  public isLogin(): boolean {
    if (this.route.url.toUpperCase().startsWith('/LOGIN')) { 
      return true; 
    }
    else {
      return false;
    }
    
  }

  public isPublicRoute(): boolean {
    return (this.route.url.toUpperCase() === "/" || this.route.url.toUpperCase().startsWith('/PUBLIC/') || this.route.url.toUpperCase().startsWith('/REPORT-NEW-BREAKDOWN') || this.route.url.toUpperCase().startsWith('/ACTION') || this.route.url.toUpperCase().startsWith('/NEW-BREAKDOWN') || this.route.url.toUpperCase().startsWith('/BREAKDOWNS'));
    
  }

  public get availableCallcentreOperators() {
    return this.sharedDataService.callcentreOperators.filter(
      operator => !operator.isWhatsappOnlyNotificationRecipient
    );
  }

  public selectCallcentreOperator(userId: number): void {
    const operator = this.availableCallcentreOperators.find(
      availableOperator => availableOperator.userId === userId
    );
    if (!operator) {
      return;
    }

    this.selectedCallcentreOperatorId = operator.userId;
    this.authService.setCallcentreOperator(operator);
    this.dismissCallcentreOperatorModal();
  }

  public dismissCallcentreOperatorModal(): void {
    this.isCallcentreOperatorModalVisible = false;
  }

  private connectWebSockets() {
    //apply web socket connection
    if (this.websocketSubscription) {
      this.websocketSubscription.unsubscribe();
    }
    this.websocketSubscription = this.websockets.connect(`${environment.wsUrl}/${this.authService!.user!.clientId}/${this.authService!.user!.userId}/${this.authService!.user!.userType}`).asObservable()
    .pipe(
      repeat({ delay: 5000 }), // Reconnect after 5 seconds if the connection is lost
      retryWhen(errors => 
        errors.pipe(
          tap(err => {
            console.error('[WS] Got error', err);
          }),
          delay(5000)
          
        )
      )
    )
    .subscribe({next: msg => {
      //let msg: SocketMessage = JSON.parse(e.data) as SocketMessage;
        this.websockets.HandleMessage(msg);
      },
      error: error => {
        this.notification.handleError(error);
      }
    });


  }
}
